import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  COVER_DOC_LIMITS,
  COVER_EFFECT_TYPES,
  newCoverEffect,
  type CoverDoc,
  type CoverEffectType,
} from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { newCoverItemId } from '../doc/coverIds';
import { EffectTile } from './EffectTile';
import { OrderList } from './OrderList';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Effects panel (design cover-editor d3): eight effects as tiles that show the effect on this
 * cover. A switch turns an effect on; a click on the tile opens its settings in the inspector; the
 * list under the tiles sets the order. Music24 draws the effects, not AI, so the screen and the
 * file are the same.
 */
const EffectsPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { state, dispatch, readOnly } = useCoverEditor();
  const { doc, selection } = state;
  // The tiles show one effect each on the cover with no text: the effect is what the tile is about.
  const base = useMemo<CoverDoc>(
    () => ({ ...doc, texts: [], effects: [] }),
    // The tiles need a new picture only when the background or the colours change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc.background, doc.palette, doc.themeId]
  );
  const isFull = doc.effects.length >= COVER_DOC_LIMITS.effects;
  const toggle = useCallback(
    (type: CoverEffectType, isOn: boolean) => {
      const active = doc.effects.find((effect) => effect.type === type);
      if (isOn && active === undefined) {
        dispatch({ type: 'addEffect', effect: newCoverEffect(type, newCoverItemId('fx'), doc.palette) });
      } else if (!isOn && active !== undefined) {
        dispatch({ type: 'removeEffect', id: active.id });
      }
    },
    [dispatch, doc.effects, doc.palette]
  );
  const select = useCallback(
    (id: string) => dispatch({ type: 'select', selection: { kind: 'effect', id } }),
    [dispatch]
  );
  const move = useCallback((id: string, toIndex: number) => dispatch({ type: 'moveEffect', id, toIndex }), [dispatch]);
  const rows = useMemo(() => doc.effects.map((effect) => ({ id: effect.id, effectType: effect.type })), [doc.effects]);

  return (
    <>
      <h3 className={styles.paneTitle}>{intl.formatMessage(coverEditorMessages.toolEffects)}</h3>
      <div className={styles.grid2}>
        {COVER_EFFECT_TYPES.map((type) => {
          const active = doc.effects.find((effect) => effect.type === type);
          return (
            <EffectTile
              key={type}
              type={type}
              base={base}
              active={active}
              isSelected={active !== undefined && selection?.kind === 'effect' && selection.id === active.id}
              disabled={readOnly || (isFull && active === undefined)}
              onToggle={toggle}
              onSelect={select}
            />
          );
        })}
      </div>
      {rows.length > 1 && (
        <>
          <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.effectOrder)}</span>
          <OrderList
            rows={rows}
            selectedId={selection?.kind === 'effect' ? selection.id : null}
            disabled={readOnly}
            onSelect={select}
            onMove={move}
          />
        </>
      )}
      <span className={styles.hint}>{withBrandWord(intl.formatMessage(coverEditorMessages.effectsHint))}</span>
    </>
  );
};

export const EffectsPanel = TypedMemo(EffectsPanelComponent);
